import { Assets } from 'pixi.js';
import { Physics, Spine, AtlasAttachmentLoader, SkeletonJson } from '@esotericsoftware/spine-pixi-v8';
import { gameConfig } from '../config/gameConfig.js';

/** Shared atlas URL; first line of the .atlas names the page image (e.g. icons.webp) next to this file. */
const SPINE_ATLAS_URL = '/assets/spines/icons.atlas';

/** Max fraction of cell (per axis) used for fitting; one scale shared by every symbol. */
const SPINE_CELL_INSET = 1.5;

/** Uniform scale for all reel symbols; set in {@link loadSpineAssets}. */
let _globalSymbolSpineScale = 1;

/**
 * @param {import('@esotericsoftware/spine-core').SkeletonData} skeletonData
 * @param {import('@esotericsoftware/spine-pixi-v8').TextureAtlas} textureAtlas
 * @returns {{ bw: number, bh: number }}
 */
function _measureSpineSetupSize(skeletonData, textureAtlas) {
  const attachmentLoader = new AtlasAttachmentLoader(textureAtlas);
  const skeletonJson = new SkeletonJson(attachmentLoader);
  const skeleton = skeletonJson.readSkeletonData(skeletonData);
  const spine = new Spine(skeleton);
  spine.scale.set(1);
  spine.skeleton.setToSetupPose();
  spine.skeleton.updateWorldTransform(Physics.update);

  const b0 = spine.getBounds();
  const data = spine.skeleton.data;
  const bw = Math.max(
    1,
    b0.width,
    typeof data.width === 'number' && data.width > 0 ? data.width : 0,
  );
  const bh = Math.max(
    1,
    b0.height,
    typeof data.height === 'number' && data.height > 0 ? data.height : 0,
  );

  spine.destroy({ children: true });
  return { bw, bh };
}

/**
 * One scale for all symbols: tightest fit so every skeleton still fits inside the cell.
 * @param {Record<number, { spineName: string, skeletonData: unknown, textureAtlas: unknown }>} spineData
 */
function _computeGlobalSymbolSpineScale(spineData) {
  const tw = gameConfig.cellWidth * SPINE_CELL_INSET;
  const th = gameConfig.cellHeight * SPINE_CELL_INSET;
  const seen = new Set();
  let scale = Infinity;

  for (const key of Object.keys(spineData)) {
    const row = spineData[key];
    if (!row?.skeletonData || !row.textureAtlas) continue;
    const name = row.spineName;
    if (seen.has(name)) continue;
    seen.add(name);

    try {
      const { bw, bh } = _measureSpineSetupSize(row.skeletonData, row.textureAtlas);
      const s = Math.min(tw / bw, th / bh);
      scale = Math.min(scale, s);
    } catch {
      /* skip broken skeleton */
    }
  }

  _globalSymbolSpineScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
}

/**
 * Loads all Spine assets for symbols
 * @returns {Promise<Record<number, any>>}
 */
export async function loadSpineAssets() {
  const spineData = {};

  let textureAtlas;
  try {
    // spine-pixi-v8 registers a loader: fetches .atlas, parses pages, loads each page image
    // from the same directory (e.g. icons.webp — must exist beside icons.atlas).
    textureAtlas = await Assets.load(SPINE_ATLAS_URL);
  } catch (error) {
    console.error(
      'Failed to load Spine texture atlas. Ensure icons.atlas and its page image (e.g. icons.webp) exist under /assets/spines/.',
      error,
    );
    return spineData;
  }

  const spineFiles = new Set();
  for (const symbol of gameConfig.symbols) {
    if (symbol.spine) {
      spineFiles.add(symbol.spine);
    }
  }

  const loadPromises = Array.from(spineFiles).map(async (spineName) => {
    try {
      const response = await fetch(`/assets/spines/${spineName}.json`);
      const skeletonData = await response.json();

      return { spineName, skeletonData, textureAtlas };
    } catch (error) {
      console.error(`Failed to load spine asset: ${spineName}`, error);
      return null;
    }
  });

  const results = await Promise.all(loadPromises);

  for (const symbol of gameConfig.symbols) {
    const result = results.find((r) => r && r.spineName === symbol.spine);
    if (result) {
      spineData[symbol.id] = result;
    }
  }

  try {
    const response = await fetch('/assets/spines/frame.json');
    const skeletonData = await response.json();
    spineData.winFrame = { spineName: 'frame', skeletonData, textureAtlas };
  } catch (error) {
    console.warn('Failed to load win frame spine (frame.json):', error);
  }

  _computeGlobalSymbolSpineScale(spineData);

  try {
    const winAtlas = await Assets.load('/assets/spines/winning_animations/popups.atlas');
    const response = await fetch('/assets/spines/winning_animations/bigWinPopups.json');
    const skeletonData = await response.json();
    spineData.winCelebration = {
      spineName: 'winCelebration',
      skeletonData,
      textureAtlas: winAtlas,
    };
  } catch (error) {
    console.warn('Failed to load win celebration spine (winning_animations):', error);
  }

  try {
    const vineAtlas = await Assets.load('/assets/spines/vine_animations/wildVines.atlas');
    const [vine1Res, vine2Res] = await Promise.all([
      fetch('/assets/spines/vine_animations/vine1.json'),
      fetch('/assets/spines/vine_animations/vine2.json'),
    ]);
    const [vineLeftSkeleton, vineRightSkeleton] = await Promise.all([
      vine1Res.json(),
      vine2Res.json(),
    ]);
    spineData.vineLeft = {
      spineName: 'vineLeft',
      skeletonData: vineLeftSkeleton,
      textureAtlas: vineAtlas,
    };
    spineData.vineRight = {
      spineName: 'vineRight',
      skeletonData: vineRightSkeleton,
      textureAtlas: vineAtlas,
    };
  } catch (error) {
    console.warn('Failed to load vine wild spines (vine_animations):', error);
  }

  return spineData;
}

/**
 * Creates a Spine instance from loaded data
 * @param {number} symbolId
 * @param {Record<number, any>} spineAssets
 * @returns {Spine | null}
 */
export function createSpineInstance(symbolId, spineAssets) {
  const spineResource = spineAssets[symbolId];
  if (!spineResource) {
    console.warn(`No spine data for symbol ${symbolId}`);
    return null;
  }

  try {
    const { skeletonData, textureAtlas } = spineResource;

    const attachmentLoader = new AtlasAttachmentLoader(textureAtlas);
    const skeletonJson = new SkeletonJson(attachmentLoader);

    const skeleton = skeletonJson.readSkeletonData(skeletonData);

    const spine = new Spine(skeleton);

    if (spine && spine.skeleton) {
      spine.skeleton.setToSetupPose();
      spine.skeleton.updateWorldTransform(Physics.update);

      spine.scale.set(_globalSymbolSpineScale);
      spine.skeleton.updateWorldTransform(Physics.update);

      const b = spine.getBounds();
      spine.x = -(b.x + b.width * 0.5);
      spine.y = -(b.y + b.height * 0.5);

      if (spine.state) {
        try {
          const idleName =
            gameConfig.animations?.idle && spine.skeleton.data.findAnimation(gameConfig.animations.idle)
              ? gameConfig.animations.idle
              : spine.skeleton.data.findAnimation('idle')
                ? 'idle'
                : null;
          if (idleName) {
            spine.state.setAnimation(0, idleName, true);
          }
        } catch (e) {
          console.warn('Could not set initial idle animation:', e);
        }
      }

      return spine;
    }
  } catch (error) {
    console.error(`Failed to create spine instance for symbol ${symbolId}:`, error);
  }

  return null;
}

/**
 * Decorative frame around a winning cell (uses {@link spineData.winFrame} from {@link loadSpineAssets}).
 * @param {Record<string | number, any>} spineAssets
 * @returns {import('@esotericsoftware/spine-pixi-v8').Spine | null}
 */
export function createWinFrameSpineInstance(spineAssets) {
  const spineResource = spineAssets?.winFrame;
  if (!spineResource?.skeletonData || !spineResource?.textureAtlas) {
    return null;
  }

  try {
    const { skeletonData, textureAtlas } = spineResource;
    const attachmentLoader = new AtlasAttachmentLoader(textureAtlas);
    const skeletonJson = new SkeletonJson(attachmentLoader);
    const skeleton = skeletonJson.readSkeletonData(skeletonData);
    const spine = new Spine(skeleton);
    if (!spine?.skeleton) return null;

    spine.skeleton.setToSetupPose();
    spine.skeleton.updateWorldTransform(Physics.update);
    spine.scale.set(_globalSymbolSpineScale);
    spine.skeleton.updateWorldTransform(Physics.update);

    const b = spine.getBounds();
    spine.x = -(b.x + b.width * 0.5);
    spine.y = -(b.y + b.height * 0.5);

    spine.visible = false;
    return spine;
  } catch (error) {
    console.error('Failed to create win frame spine:', error);
    return null;
  }
}

/**
 * Decorative expanding-wild vine on reel side.
 * @param {Record<string | number, any>} spineAssets
 * @param {'left' | 'right'} side
 * @returns {import('@esotericsoftware/spine-pixi-v8').Spine | null}
 */
export function createVineSpineInstance(spineAssets, side = 'left') {
  const key = side === 'right' ? 'vineRight' : 'vineLeft';
  const spineResource = spineAssets?.[key];
  if (!spineResource?.skeletonData || !spineResource?.textureAtlas) {
    return null;
  }

  try {
    const { skeletonData, textureAtlas } = spineResource;
    const attachmentLoader = new AtlasAttachmentLoader(textureAtlas);
    const skeletonJson = new SkeletonJson(attachmentLoader);
    const skeleton = skeletonJson.readSkeletonData(skeletonData);
    const spine = new Spine(skeleton);
    if (!spine?.skeleton) return null;

    spine.skeleton.setToSetupPose();
    spine.skeleton.updateWorldTransform(Physics.update);
    const b = spine.getBounds();
    const targetH = Math.max(1, gameConfig.rowCount * gameConfig.cellHeight * 1.02);
    const fitScale = b.height > 0 ? targetH / b.height : 1;
    spine.scale.set(fitScale);
    spine.skeleton.updateWorldTransform(Physics.update);

    const fitB = spine.getBounds();
    spine.x = -(fitB.x + fitB.width * 0.5);
    spine.y = -(fitB.y + fitB.height * 0.5);
    return spine;
  } catch (error) {
    console.error(`Failed to create vine spine (${side}):`, error);
    return null;
  }
}
